'use client';
import React from 'react';

const BASE = 'https://cullertons-b4f8396.public.builtwithrocket.new';

export default function LocalitySection() {
  return (
    <section id="locality" className="py-24 md:py-32 px-6 md:px-12 bg-warm-white">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="reveal">
            <span className="text-[11px] uppercase tracking-[0.2em] text-forest font-semibold block mb-3">WHY THE NEW TOWN</span>
            <h2
              className="font-serif text-forest leading-[0.92] tracking-tight"
              style={{ fontSize: 'clamp(2.4rem, 5vw, 4.5rem)' }}>

              The capital&apos;s most<br />
              <span className="italic" style={{ color: '#CFB786' }}>exclusive suburb.</span>
            </h2>
          </div>
          <p className="text-forest/65 max-w-sm leading-relaxed text-base reveal delay-100">
            Edinburgh&apos;s New Town offers refined city living at its best. From iconic Georgian architecture, elegant garden squares to world-class amenities, residents are drawn to its timeless beauty, walkability and the rare balance of prestige and everyday convenience.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {/* Large photo - Corstorphine Hill */}
          <div className="bento-card col-span-12 md:col-span-7 md:row-span-2 reveal photo-hover" style={{ minHeight: '260px' }}>
            <div className="absolute inset-0">
              <img
                alt="St James Quarter shopping and leisure development in Edinburgh city centre"
                src="/assets/images/st_james_quarter-1776852812060.jpg"
                className="object-cover w-full h-full"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, objectFit: 'cover' }} />

            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-7 z-10">
              <span className="block font-serif text-cream text-sm md:text-base leading-tight italic">St James Quarter, New Town</span>
              <span className="block text-cream/65 text-xs mt-1 font-medium">Offering world-class shopping, dining, and entertainment, in the New Town&nbsp;</span>
            </div>
          </div>

          {/* EH4 stat */}
          <div className="bento-card col-span-6 md:col-span-3 bg-forest reveal p-6 md:p-8 flex flex-col justify-between" style={{ minHeight: '160px' }}>
            <span className="font-serif text-4xl md:text-5xl leading-none text-cream">EH3</span>
            <div>
              <span className="block text-sm font-semibold uppercase tracking-widest text-cream">EDINBURGH'S PREMIER POSTCODE</span>
              <span className="block text-xs mt-1 text-cream/60">Consistently rated as the top choice for local and international buyers</span>
            </div>
          </div>

          {/* £800k stat */}
          <div className="bento-card col-span-6 md:col-span-2 bg-sage reveal p-6 md:p-8 flex flex-col justify-between" style={{ minHeight: '160px' }}>
            <span className="font-serif text-4xl md:text-5xl leading-none text-cream">£800k</span>
            <div>
              <span className="block text-sm font-semibold uppercase tracking-widest text-cream">AVG. PRICE</span>
              <span className="block text-xs mt-1 text-cream/60">that Cullerton's achieve, New Town</span>
            </div>
          </div>

          {/* Craigcrook photo */}
          <div className="bento-card col-span-12 md:col-span-3 reveal delay-100 photo-hover" style={{ minHeight: '260px' }}>
            <div className="absolute inset-0">
              <img
                alt="Princes Street, Edinburgh at night"
                src="/assets/images/Princes_Street-1776856773500.jpg"
                className="object-cover w-full h-full"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, objectFit: 'cover' }} />

            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-7 z-10">
              <span className="block font-serif text-cream text-sm md:text-base leading-tight italic">UNESCO World Heritage Site</span>
              <span className="block text-cream/65 text-xs mt-1 font-medium">Edinburgh's New Town </span>
            </div>
          </div>

          {/* Schools */}
          <div className="bento-card col-span-12 md:col-span-2 bg-[#3F5948] reveal delay-100 p-6 md:p-8 flex flex-col justify-between" style={{ minHeight: '160px' }}>
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/50 font-semibold">GALLERIES & MUSEUMS</span>
            <p className="text-white/80 text-sm leading-relaxed font-light">Royal Scottish Academy of Art · National Portrait Gallery · National Galleries of Scotland</p>
          </div>

          {/* Ravelston Woods */}
          <div className="bento-card col-span-6 md:col-span-4 reveal delay-100 photo-hover" style={{ minHeight: '260px' }}>
            <div className="absolute inset-0">
              <img
                alt="Royal Botanic Garden Edinburgh with lush greenery and glasshouses"
                src="/assets/images/Royal_Botanic_Gardens-1776855276502.jpg"
                className="object-cover w-full h-full"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, objectFit: 'cover' }} />

            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-7 z-10">
              <span className="block font-serif text-cream text-sm md:text-base leading-tight italic">Royal Botanic Garden</span>
              <span className="block text-cream/65 text-xs mt-1 font-medium">Located at the foot of the New Town</span>
            </div>
          </div>

          {/* 1930s Character Homes */}
          <div className="bento-card col-span-6 md:col-span-4 reveal delay-200 photo-hover" style={{ minHeight: '260px' }}>
            <div className="absolute inset-0">
              <img
                alt="Historic Edinburgh theatre interior with ornate architecture and stage lighting"
                src="/assets/images/Theatre-1776855841056.jpg"
                className="object-cover w-full h-full"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, objectFit: 'cover' }} />

            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-7 z-10">
              <span className="block font-serif text-cream text-sm md:text-base leading-tight italic">Culture on your doorstep</span>
              <span className="block text-cream/65 text-xs mt-1 font-medium">Renowned for its theatres, museums, galleries and festivals</span>
            </div>
          </div>

          {/* Tree-lined street */}
          <div className="bento-card col-span-12 md:col-span-4 reveal delay-200 photo-hover" style={{ minHeight: '260px' }}>
            <div className="absolute inset-0">
              <img
                alt="Wide tree-lined street in Blackhall at sunset with golden light filtering through tall trees lining a quiet residential road"
                src="/assets/images/The_Dome_George_Street-1776856336087.jpg"
                className="object-cover w-full h-full"
                style={{ position: 'absolute', height: '100%', width: '100%', left: 0, top: 0, objectFit: 'cover' }} />

            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-forest/75 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-5 md:p-7 z-10">
              <span className="block font-serif text-cream text-sm md:text-base leading-tight italic">Breath-taking architecture : The Dome on George Street, New Town</span>
            </div>
          </div>
        </div>
      </div>
    </section>);

}